import type { DomainEvent } from "@szyos/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";

export interface LiveState {
  connected: boolean;
  /** Zeitpunkt des zuletzt bestätigten Ereignisses oder Verbindungsaufbaus. */
  lastConfirmedAt: Date | null;
  lastEvent: DomainEvent | null;
}

const LiveContext = createContext<LiveState>({ connected: false, lastConfirmedAt: null, lastEvent: null });

/** Welche Abfragen ein Ereignis veraltet macht. */
function keysFor(e: DomainEvent): string[][] {
  const keys: string[][] = [["work-orders"], ["recent-events"], ["costs"], ["usage"]];
  const woId =
    (e.payload.workOrderId as string | undefined) ??
    (e.aggregateType === "work_order" ? e.aggregateId : undefined);
  if (woId) keys.push(["work-order", woId], ["wo-events", woId]);
  if (e.aggregateType === "decision" || e.eventType.startsWith("decision.")) keys.push(["decisions"]);
  if (e.aggregateType === "idea" || e.eventType.startsWith("idea.")) keys.push(["ideas"]);
  if (e.aggregateType === "project" || e.eventType === "idea.promoted") keys.push(["projects"]);
  if (e.aggregateType === "node") keys.push(["nodes"]);
  if (e.aggregateType === "studio_session") keys.push(["studio"], ["ideas"]);
  if (e.eventType.startsWith("killswitch.")) keys.push(["killswitch"]);
  if (
    e.aggregateType === "node" ||
    e.eventType === "provider_auth.changed" ||
    e.eventType === "run.quota_exhausted"
  )
    keys.push(["provider-auth"]);
  return keys;
}

/**
 * Live-Verbindung per SSE. Bei Abbruch setzt der Browser mit Last-Event-ID fort;
 * die Oberfläche zeigt dann „Live pausiert“ mit dem letzten bestätigten Zeitpunkt.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LiveState>({ connected: false, lastConfirmedAt: null, lastEvent: null });
  const pending = useRef(new Set<string>());
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const source = new EventSource("/api/v1/stream");
    const flush = () => {
      timer.current = null;
      for (const key of pending.current)
        void queryClient.invalidateQueries({ queryKey: JSON.parse(key) as string[] });
      pending.current.clear();
    };
    source.onopen = () => setState((s) => ({ ...s, connected: true, lastConfirmedAt: new Date() }));
    source.onerror = () => setState((s) => ({ ...s, connected: false }));
    source.addEventListener("domain", (msg) => {
      try {
        const event = JSON.parse((msg as MessageEvent<string>).data) as DomainEvent;
        for (const k of keysFor(event)) pending.current.add(JSON.stringify(k));
        if (timer.current === null) timer.current = window.setTimeout(flush, 150);
        setState({ connected: true, lastConfirmedAt: new Date(), lastEvent: event });
      } catch {
        /* defektes Ereignis ignorieren, der nächste Abruf korrigiert den Stand */
      }
    });
    return () => {
      source.close();
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [queryClient]);

  return <LiveContext.Provider value={state}>{children}</LiveContext.Provider>;
}

export function useLive(): LiveState {
  return useContext(LiveContext);
}
